import _ from 'lodash';
import { initPortfolioDialog } from 'pages/mockup/assistFunc';
import { getUserLKKey } from 'src/model/helpers';
import { consumeStoredRedirectUrl, normalizeRedirectUrl } from 'src/services/authAccount/redirectUrl.service';
import { queryBot2, queryOne } from 'boot/helpers/queries';

function isProfileEntryRoute (to) {
    return to.path === '/user'
        || to.path === '/user/main-info';
}

function isTemporaryUserLinkRoute (to) {
    const isRedirectFromUser = to.redirectedFrom?.path === '/user';

    return Boolean(to.query?.t)
        && !to.query?.key
        && isRedirectFromUser;
}

async function getTemporaryUserLinkKey (uuid) {
    const safeUuid = String(uuid).replace(/'/g, "''");
    const data = await queryOne(`
        select students.chat_id
        from temporary_urls
        join students on students.id = temporary_urls."userId"
        where temporary_urls.uuid = '${safeUuid}'
          and temporary_urls."createdAt" + make_interval(hours => temporary_urls."lifeHours") >= now()
        limit 1
    `);

    if (!data?.chat_id) {
        return null;
    }

    return getUserLKKey(data.chat_id);
}

/**
 *
 * @param to
 * @param user
 * @returns {Promise<boolean>}
 */
async function isPortfolioBreakRoute (to, user) {
    if (to.path === '/user/portfolio') {
        const { data } = await queryBot2('check-user-portfolio', { untiId: user.unti_id });

        if (_.isEmpty(data)) {
            initPortfolioDialog();

            return true;
        }
    }

    return false;
}

/**
 * Prevents entering portfolio page if user has no portfolio data.
 *
 * @param {object} ctx
 * @param {import('vue-router').RouteLocationNormalized} ctx.to
 * @param {import('vue-router').RouteLocationNormalized} ctx.from
 * @param {any} ctx.user
 * @returns {Promise<false|string>} false to continue; from.fullPath to return back.
 */
export async function rulePortfolioBreak ({ to, user, from }) {
    // console.log('🔍 проверка на портфолио');

    if (!user) {
        return false;
    }

    if (await isPortfolioBreakRoute(to, user)) {
        return from.fullPath;
    }

    return false;
}

/**
 * Requires authentication for /code-hub routes.
 *
 * @param {object} ctx
 * @param {import('vue-router').RouteLocationNormalized} ctx.to
 * @param {any} ctx.user
 * @returns {false|string} false to continue; string path to redirect.
 */
export function ruleCodeHub ({ to, user }) {
    // console.log('🔍 проверка на codehub');

    if (!user && to.path.startsWith('/code-hub')) {
        return '/login';
    }

    return false;
}

/**
 * Redirect helper for /user?redirectUrl=... route.
 *
 * @param {object} ctx
 * @param {import('vue-router').RouteLocationNormalized} ctx.to
 * @returns {false|string} false to continue; string URL to redirect.
 */
export function ruleUserRedirect ({ to }) {
    // console.log('🔍 редиректы для пользователя');

    if (isProfileEntryRoute(to)) {
        const redirectUrl = normalizeRedirectUrl(to.query?.redirectUrl) || consumeStoredRedirectUrl();

        if (redirectUrl && redirectUrl !== `${to.path}${to.fullPath.slice(to.path.length)}`) {
            return redirectUrl;
        }

        return false;
    }

    return false;
}

/**
 * Resolves /user?t=<uuid> temporary links into the standard key-based auth flow.
 *
 * @param {object} ctx
 * @param {import('vue-router').RouteLocationNormalized} ctx.to
 * @returns {Promise<false|object>}
 */
export async function ruleTemporaryUserRedirect ({ to }) {
    if (!isTemporaryUserLinkRoute(to)) {
        return false;
    }

    const key = await getTemporaryUserLinkKey(to.query.t);

    if (!key) {
        return { path: '/login' };
    }

    sessionStorage.setItem('is-auth-by-temp-link', 'true');

    return {
        path: '/user',
        query: {
            ..._.omit(to.query, ['t']),
            key,
        },
    };
}

/**
 * Blocks competencies page when local mode is disabled.
 *
 * @param {object} ctx
 * @param {import('vue-router').RouteLocationNormalized} ctx.to
 * @param {any} ctx.store
 * @returns {false|object} false to continue; route object to redirect.
 */
export function ruleCompetenciesLocalMode ({ to, store }) {
    // console.log('🔍 проверка на локальный режим компетенций');

    if (to.name === 'user-competencies' && !store?.state?.platform?.settings?.local_mode_irpo) {
        return { name: 'profile-root' };
    }

    return false;
}
